import React, { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { motion } from 'framer-motion';
import { X, Loader2 } from 'lucide-react';
import { addToWaitlist } from '../lib/supabase';
import toast, { Toaster } from 'react-hot-toast';

interface SignupModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SignupModal: React.FC<SignupModalProps> = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    
    try {
      const result = await addToWaitlist(email);
      
      if (result.success) {
        setStatus('success');
        toast.success('Welcome to the waitlist! 🎉');
        setTimeout(() => {
          onClose();
          setStatus('idle');
          setEmail('');
        }, 2000);
      } else {
        throw new Error(result.error || 'Failed to join waitlist');
      }
    } catch (error) {
      setStatus('error');
      toast.error(error instanceof Error ? error.message : 'Failed to join waitlist');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  return (
    <>
      <Toaster position="top-center" />
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden glass p-6 text-left align-middle shadow-xl transition-all">
                  <div className="absolute right-4 top-4">
                    <button
                      onClick={onClose}
                      className="glass-button p-2 rounded-full"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  <Dialog.Title
                    as="h3"
                    className="text-2xl font-bold leading-6 mb-4"
                  >
                    Be First to Know
                  </Dialog.Title>

                  <p className="text-gray-300 mb-6">
                    Join our waitlist to get early access and exclusive launch offers for RunAI Coach.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email"
                        className="w-full px-4 py-3 glass rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        required
                        disabled={status === 'loading' || status === 'success'}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={status === 'loading' || status === 'success'}
                      className={`w-full glass-button py-3 px-4 font-medium flex items-center justify-center gap-2 ${
                        status === 'loading' ? 'opacity-70' : ''
                      } ${
                        status === 'success' ? 'bg-green-500/20 text-green-300' : ''
                      } ${
                        status === 'error' ? 'bg-red-500/20 text-red-300' : ''
                      }`}
                    >
                      {status === 'loading' && <Loader2 className="w-4 h-4 animate-spin" />}
                      {status === 'loading' ? 'Joining...' :
                       status === 'success' ? 'Welcome aboard! 🎉' :
                       status === 'error' ? 'Try again' :
                       'Join Waitlist'}
                    </button>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};